import { animate, style, transition, trigger } from '@angular/animations';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
} from '@angular/core';
import { ChipContentOption } from 'src/app/shared/models/chip-content/chip-content-option';

@Component({
  selector: 'app-chip',
  templateUrl: './chip.component.html',
  styleUrls: ['./chip.component.scss'],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('0ms', style({ opacity: 1 })),
      ]),
      transition(':leave', [
        style({ opacity: 1 }),
        animate('0ms', style({ opacity: 0 })),
      ]),
    ]),
  ],
})
/**
 * Chip atom
 */
export class ChipComponent {
  private _isSmall = false;
  private _isGray = false;
  private _isLightGray = false;
  private _isFilled = false;
  private _isGreen = false;
  private _isRed = false;
  private _isGrayBlue = false;
  private _isDarkBlue = false;
  private _isYellow = false;
  private _isActive?: boolean;
  @Input() options?: ChipContentOption[];
  @Input() icon?: string;
  @Input() iconAfter?: string;
  @Output() selectFilterClick = new EventEmitter<any>();
  @Output() optionClickAction = new EventEmitter<any>();
  private activated = false;
  private activatedGrayBlue = false;
  showOptions: boolean = false;

  /**
   * Decides if this label is smaller
   * @return {boolean}
   */
  @Input()
  get small(): boolean {
    return this._isSmall;
  }

  /**
   * Sets this label as a smaller one by using an html attribute
   * @param {any} value
   */
  set small(value: any) {
    this._isSmall = coerceBooleanProperty(value);
  }

  /**
   * Decides if this label is gray
   * @return {boolean}
   */
  @Input()
  get gray(): boolean {
    return this._isGray;
  }

  /**
   * Sets this label as a gray one by using an html attribute
   * @param {any} value
   */
  set gray(value: any) {
    this._isGray = coerceBooleanProperty(value);
  }

  /**
   * Decides if this label is green
   * @return {boolean}
   */
  @Input()
  get green(): boolean {
    return this._isGreen;
  }

  /**
   * Sets this label as a green one by using an html attribute
   * @param {any} value
   */
  set green(value: any) {
    this._isGreen = coerceBooleanProperty(value);
  }

  /**
   * Decides if this label is light gray
   * @return {boolean}
   */
  @Input()
  get lightgray(): boolean {
    return this._isLightGray;
  }

  /**
   * Sets this label as a green one by using an html attribute
   * @param {any} value
   */
  set lightgray(value: any) {
    this._isLightGray = coerceBooleanProperty(value);
  }

  /**
   * Decides if this label is filled
   * @return {boolean}
   */
  @Input()
  get filled(): boolean {
    return this._isFilled;
  }

  /**
   * Sets this label as a filled one by using an html attribute
   * @param {any} value
   */
  set filled(value: any) {
    this._isFilled = coerceBooleanProperty(value);
  }

  /**
   * Decides if this label is red
   * @return {boolean}
   */
  @Input()
  get red(): boolean {
    return this._isRed;
  }

  /**
   * Sets this label as a red one by using an html attribute
   * @param {any} value
   */
  set red(value: any) {
    this._isRed = coerceBooleanProperty(value);
  }

  /**
   * Decides if this label is active
   * @return {boolean}
   */
  @Input()
  get active(): boolean {
    return this._isActive != null ? this._isActive : this.activated;
  }

  /**
   * Sets this label as a active one by using an html attribute
   * @param {any} value
   */
  set active(value: any) {
    this._isActive = coerceBooleanProperty(value);
  }

  /**
   * Decides if this label is active
   * @return {boolean}
   */
  @Input()
  get activeGrayBlue(): boolean {
    return this._isActive != null ? this._isActive : this.activatedGrayBlue;
  }

  /**
   * Sets this label as a active one by using an html attribute
   * @param {any} value
   */
  set activeGrayBlue(value: any) {
    this._isActive = coerceBooleanProperty(value);
  }
  /**
   * Decides if this label is active with blue border
   * @return {boolean}
   */
  @Input()
  get grayBlue(): boolean {
    return this._isGrayBlue != null ? this._isGrayBlue : this.activated;
  }

  /**
   * Sets this label as a active with blue border one by using an html attribute
   * @param {any} value
   */
  set grayBlue(value: any) {
    this._isGrayBlue = coerceBooleanProperty(value);
  }

  /**
   * Decides if this label is dark blue
   * @return {boolean}
   */
  @Input()
  get darkBlue(): boolean {
    return this._isDarkBlue;
  }

  /**
   * Sets this label as a dark blue one by using an html attribute
   * @param {any} value
   */
  set darkBlue(value: any) {
    this._isDarkBlue = coerceBooleanProperty(value);
  }

  /**
   * Decides if this label is yellow
   * @return {boolean}
   */
  @Input()
  get yellow(): boolean {
    return this._isYellow;
  }

  /**
   * Sets this label as a yellow one by using an html attribute
   * @param {any} value
   */
  set yellow(value: any) {
    this._isYellow = coerceBooleanProperty(value);
  }

  /**
   * Check button state
   */
  private toggleItem() {
    if (this.options) {
      this.showOptions = true;
    }
    this.activated = !this.activated;
    this.activatedGrayBlue = !this.activatedGrayBlue;
  }

  /**
   * Emits button click event for underlying material button
   */
  emitClick() {
    this.selectFilterClick.emit();
    this.toggleItem();
  }

  /**
   * Emits click action event for underlying material button
   * @param {string} option
   */
  emitClickOption(option: ChipContentOption) {
    this.optionClickAction.emit(option);
    this.showOptions = !this.showOptions;
  }

  /**
   * Get click event
   * @param {any} event
   */
  @HostListener('window:click', ['$event'])
  onDocumentClick() {
    this.showOptions = false;
  }
}
